import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { useNavigate } from 'react-router-dom';

/* ─── Math pre-processor ─────────────────────────────────────────── */
const preprocessMath = (text) => {
  if (!text) return text;
  text = text.replace(/\\\[(.*?)\\\]/gs, '$$$$$1$$$$');
  text = text.replace(/\\\((.*?)\\\)/gs, '$$$1$$');
  return text;
};



/* ─── ChatUI Component ───────────────────────────────────────────── */
const ChatUI = () => {
  const { token, logout } = useAuth();
  const navigate = useNavigate();

  /* state */
  const [messages, setMessages]           = useState([]);
  const [inputMessage, setInputMessage]   = useState('');
  const [isLoading, setIsLoading]         = useState(false);
  const [isAdmin, setIsAdmin]             = useState(false);
  const [fullName, setFullName]           = useState('');
  const [sessions, setSessions]           = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [isListening, setIsListening]     = useState(false);
  const [showDocMenu, setShowDocMenu]     = useState(false);
  const [showMemoModal, setShowMemoModal] = useState(false);
  const [memoFormData, setMemoFormData]   = useState({
    date: '', subject: '', to_person: '', content: '', sign_name: '', position: ''
  });
  const [speakingIndex, setSpeakingIndex] = useState(null);
  const [isMobile, setIsMobile]           = useState(window.innerWidth <= 768);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);

  /* refs */
  const messagesEndRef    = useRef(null);
  const skipFetchHistory  = useRef(false);
  const recognitionRef    = useRef(null);
  const textareaRef       = useRef(null);
  const audioRef          = useRef(null);

  /* ── Scroll to bottom ──────────────────────────────────────────── */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /* ── TTS Cleanup & Preload ─────────────────────────────────────── */
  useEffect(() => {
    if ('speechSynthesis' in window) {
      // Preload voices
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
    }
    return () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  /* ── Responsive sidebar ────────────────────────────────────────── */
  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile !== isMobile) setIsSidebarOpen(!mobile);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isMobile]);

  /* ── Decode JWT ────────────────────────────────────────────────── */
  useEffect(() => {
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setIsAdmin(payload.is_admin === true);
      setFullName(payload.full_name || payload.sub || '');
    } catch {
      setIsAdmin(false);
      setFullName('');
    }
  }, [token]);

  /* ── Speech recognition ────────────────────────────────────────── */
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = 'th-TH';
    rec.onresult = (e) => {
      let transcript = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) transcript += e.results[i][0].transcript;
      }
      if (transcript) setInputMessage(p => p ? `${p} ${transcript}` : transcript);
      setIsListening(false);
    };
    rec.onerror = (e) => {
      setIsListening(false);
      if (e.error === 'not-allowed')
        alert('กรุณาอนุญาตการใช้งานไมโครโฟน (ต้องใช้ HTTPS หรือ localhost)');
      else if (e.error !== 'no-speech')
        alert('ระบบรับเสียงมีปัญหา: ' + e.error);
    };
    rec.onend = () => setIsListening(false);
    recognitionRef.current = rec;
  }, []);

  /* ── Auto-resize textarea ──────────────────────────────────────── */
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, [inputMessage]);

  /* ── Fetch sessions ────────────────────────────────────────────── */
  const fetchSessions = useCallback(async () => {
    try {
      const res = await fetch(`/api/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setSessions(await res.json());
    } catch (e) {
      console.error('Failed to fetch sessions:', e);
    }
  }, [token]);

  /* ── Logout ────────────────────────────────────────────────────── */
  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  useEffect(() => {
    if (token) fetchSessions();
  }, [fetchSessions, token]);

  /* ── Fetch chat history ────────────────────────────────────────── */
  useEffect(() => {
    const load = async () => {
      if (skipFetchHistory.current) { skipFetchHistory.current = false; return; }
      if (!currentSessionId) { setMessages([]); return; }
      try {
        const res = await fetch(
          `/api/sessions/${currentSessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.ok) {
          const data = await res.json();
          setMessages(data.messages || []);
        } else if (res.status === 401) {
          handleLogout();
        }
      } catch (e) {
        console.error('Failed to fetch history:', e);
      }
    };
    if (token) load();
  }, [token, currentSessionId, handleLogout]);

  /* ── Handlers ──────────────────────────────────────────────────── */
  const handleNewChat = () => {
    setCurrentSessionId(null);
    setMessages([]);
    if (isMobile) setIsSidebarOpen(false);
  };

  const toggleSpeak = async (text, index) => {
    // หากกำลังเล่นเสียงของข้อความเดิมอยู่ ให้หยุดเล่น
    if (speakingIndex === index && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setSpeakingIndex(null);
      return;
    }

    // หยุดเสียงที่กำลังเล่นอยู่ (ถ้ามี)
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    // ทำความสะอาดข้อความ
    let plainText = text;
    plainText = plainText.replace(/[*_#`]/g, ''); 
    plainText = plainText.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    
    if (!plainText.trim()) return;

    setSpeakingIndex(index);

    try {
      const response = await fetch(`/api/tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ text: plainText })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch TTS audio');
      }

      const blob = await response.blob();
      const audioUrl = URL.createObjectURL(blob);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onended = () => {
        setSpeakingIndex(null);
        URL.revokeObjectURL(audioUrl); // คืน memory
      };

      audio.onerror = () => {
        setSpeakingIndex(null);
        alert('เกิดข้อผิดพลาดในการเล่นเสียง');
      };

      await audio.play();
    } catch (error) {
      console.error("TTS Error:", error);
      setSpeakingIndex(null);
      alert('ไม่สามารถอ่านออกเสียงได้ กรุณาลองใหม่อีกครั้ง');
    }
  };

  const handleDeleteSession = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('ต้องการลบการสนทนานี้หรือไม่?')) return;
    try {
      await fetch(`/api/sessions/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (currentSessionId === id) handleNewChat();
      fetchSessions();
    } catch (err) {
      console.error(err);
    }
  };

  const toggleListen = (e) => {
    e.preventDefault();
    if (!recognitionRef.current) { alert('เบราว์เซอร์ไม่รองรับการรับเสียง'); return; }
    if (isListening) { recognitionRef.current.stop(); }
    else { recognitionRef.current.start(); setIsListening(true); }
  };

  const downloadContent = (content, defaultFilename) => {
    const filename = window.prompt('ระบุชื่อไฟล์ (เช่น code.py, index.html):', defaultFilename);
    if (!filename) return;
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = Object.assign(document.createElement('a'), { href: url, download: filename });
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };



  const handleDocSelect = (docType) => {
    setShowDocMenu(false);
    if (docType === 'บันทึกข้อความ') {
      setShowMemoModal(true);
    } else {
      alert(`ฟีเจอร์ "${docType}" พบกันเร็วๆ นี้ครับ!`);
    }
  };

  const handleMemoSubmit = (e) => {
    e.preventDefault();
    setShowMemoModal(false);
    const prompt = `ช่วยสร้างบันทึกข้อความให้หน่อยครับ โดยมีรายละเอียดดังนี้:
- วันที่: ${memoFormData.date || '-'}
- เรื่อง: ${memoFormData.subject || '-'}
- เรียน: ${memoFormData.to_person || '-'}
- เนื้อหา: ${memoFormData.content || '-'}
- ลงชื่อ: ${memoFormData.sign_name || '-'}
- ตำแหน่ง: ${memoFormData.position || '-'}`;
    setInputMessage(p => (p ? p + '\n' : '') + prompt);
    // Note: User will still need to press send, which is consistent with the UI.
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;
    const newMsg = { role: 'user', content: inputMessage };
    setMessages(p => [...p, newMsg, { role: 'assistant', content: '' }]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const res = await fetch(`/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: newMsg.content, session_id: currentSessionId }),
      });
      if (res.status === 401) { handleLogout(); return; }
      if (!res.ok) throw new Error('Network error');

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let firstMsg = true;
      let buf = '';
      let done = false;

      while (!done) {
        const { value, done: d } = await reader.read();
        done = d;
        if (value) {
          buf += decoder.decode(value, { stream: true });
          const lines = buf.split('\n');
          buf = lines.pop();
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const raw = line.slice(6).trim();
            if (!raw) continue;
            try {
              const data = JSON.parse(raw);
              if (firstMsg && data.session_id) {
                if (!currentSessionId) {
                  skipFetchHistory.current = true;
                  setCurrentSessionId(data.session_id);
                  fetchSessions();
                }
                firstMsg = false;
                continue;
              }
              if (data.error || data.content) {
                setMessages(prev => {
                  const arr = [...prev];
                  const last = arr.length - 1;
                  if (arr[last]?.role === 'assistant') {
                    arr[last] = {
                      ...arr[last],
                      content: data.error
                        ? `**Error:** ${data.error}`
                        : arr[last].content + data.content,
                    };
                  }
                  return arr;
                });
              }
            } catch (ex) {
              console.error('SSE parse error', ex);
            }
          }
        }
      }
    } catch (err) {
      console.error('Send error:', err);
      setMessages(p => [...p, { role: 'system', content: 'เกิดข้อผิดพลาดในการสื่อสารกับเซิร์ฟเวอร์' }]);
    } finally {
      setIsLoading(false);
    }
  };

  /* ── User initials ─────────────────────────────────────────────── */
  const initials = fullName
    ? fullName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  /* ── Render ────────────────────────────────────────────────────── */
  return (
    <div className="chat-bg">

      {/* Mobile backdrop */}
      {isSidebarOpen && isMobile && (
        <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />
      )}

      {/* Desktop sidebar spacer (push main content) */}
      <div
        className="sidebar-spacer"
        style={{ width: isSidebarOpen && !isMobile ? 'var(--sidebar-width)' : 0 }}
      />

      {/* ── Sidebar ────────────────────────────────────────────────── */}
      <aside className={`sidebar${isSidebarOpen ? '' : ' sidebar--closed'}`}>

        {/* Brand header */}
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <div className="sidebar-logo">
              <img src="/image/logoalex.png" alt="logo" className="sidebar-logo-img" />
            </div>
            <h1 className="sidebar-title">alexAI</h1>
          </div>
          {isMobile && (
            <button className="btn-icon" onClick={() => setIsSidebarOpen(false)} title="ปิดเมนู">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          )}
        </div>

        {/* New chat button */}
        <div className="sidebar-actions">
          <button className="new-chat-btn" onClick={handleNewChat}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            New Chat
          </button>
        </div>

        {/* Session list */}
        <div className="sidebar-section-title">Recent</div>
        <div className="sidebar-sessions">
          {sessions.length === 0 && (
            <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-subtle)', padding: '0.5rem 0.75rem' }}>
              ยังไม่มีการสนทนา
            </p>
          )}
          {sessions.map(s => (
            <div
              key={s.id}
              className={`session-item${currentSessionId === s.id ? ' session-item--active' : ''}`}
              onClick={() => { setCurrentSessionId(s.id); if (isMobile) setIsSidebarOpen(false); }}
            >
              <div className="session-item__label">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ flexShrink: 0, opacity: 0.6 }}>
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                {s.title}
              </div>
              <button
                className="session-item__delete"
                onClick={(e) => handleDeleteSession(e, s.id)}
                title="ลบการสนทนา"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
              </button>
            </div>
          ))}
        </div>

        {/* Footer – user info + buttons */}
        <div className="sidebar-footer">
          {/* user chip */}
          <div className="user-chip">
            <div className="user-avatar">{initials}</div>
            <span className="user-name">{fullName || 'ผู้ใช้งาน'}</span>
            {isAdmin && <span className="badge-admin">Admin</span>}
          </div>

          {isAdmin && (
            <button
              className="btn btn-ghost"
              style={{ width: '100%', fontSize: 'var(--font-size-sm)', padding: '0.5rem 1rem' }}
              onClick={() => navigate('/admin')}
            >
              Admin Panel
            </button>
          )}
          <button
            className="btn btn-ghost"
            style={{ width: '100%', fontSize: 'var(--font-size-sm)', padding: '0.5rem 1rem' }}
            onClick={handleLogout}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            ออกจากระบบ
          </button>
        </div>
      </aside>

      {/* ── Main chat area ────────────────────────────────────────── */}
      <div className="chat-main">

        {/* Header */}
        <header className="chat-header">
          <button
            className="btn-icon"
            onClick={() => setIsSidebarOpen(v => !v)}
            title="Toggle Sidebar"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <line x1="9" y1="3" x2="9" y2="21"/>
            </svg>
          </button>
          <h2 className="chat-header__title">
            {sessions.find(s => s.id === currentSessionId)?.title || 'New Chat'}
          </h2>
        </header>

        {/* Messages */}
        <main className="chat-messages">
          {/* Empty state */}
          {messages.length === 0 && (
            <div className="empty-state">
              <div className="empty-state__logo">
                <img src="/image/logoalex.png" alt="alexAI" />
              </div>
              <h2 className="empty-state__greeting">สวัสดีครับ คุณ {fullName} 👋</h2>
              <p className="empty-state__tagline">"ขับเคลื่อนโรงเรียนยุคดิจิทัล ด้วยพลังแห่ง AI"</p>
              <p className="empty-state__hint">พิมพ์ข้อความด้านล่างเพื่อเริ่มการสนทนา</p>
            </div>
          )}

          {/* Message bubbles */}
          {messages.map((msg, i) => (
            <div key={i} className={`msg-row${msg.role === 'user' ? ' msg-row--user' : ''}`}>
              {/* AI avatar */}
              {msg.role === 'assistant' && (
                <div className="ai-avatar">
                  <img src="/image/logoalex.png" alt="alexAI" />
                </div>
              )}
              <div className={`msg-bubble${msg.role === 'user' ? ' msg-bubble--user' : msg.role === 'assistant' ? ' msg-bubble--ai' : ' msg-bubble--ai'}`}>
                {msg.role === 'user' ? (
                  <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                ) : msg.role === 'assistant' && msg.content === '' && isLoading ? (
                  /* Typing indicator */
                  <div className="typing-dot">
                    <span/><span/><span/>
                  </div>
                ) : (
                  <div className="markdown-body" style={{ overflowX: 'auto', position: 'relative' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                      <button 
                        onClick={() => toggleSpeak(msg.content, i)}
                        title={speakingIndex === i ? "หยุดอ่าน" : "อ่านออกเสียง"}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: speakingIndex === i ? 'var(--primary)' : 'var(--text-subtle)',
                          padding: '4px', display: 'flex', alignItems: 'center',
                          transition: 'color 0.2s', borderRadius: '50%'
                        }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--primary)'}
                        onMouseLeave={e => e.currentTarget.style.color = speakingIndex === i ? 'var(--primary)' : 'var(--text-subtle)'}
                      >
                         <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill={speakingIndex === i ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                           {speakingIndex === i ? (
                             <>
                               <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                               <line x1="23" y1="9" x2="17" y2="15"></line>
                               <line x1="17" y1="9" x2="23" y2="15"></line>
                             </>
                           ) : (
                             <>
                               <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                               <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                             </>
                           )}
                         </svg>
                      </button>
                    </div>
                    <ReactMarkdown
                      remarkPlugins={[remarkMath]}
                      rehypePlugins={[rehypeKatex]}
                      components={{
                        a({ href, children, ...props }) {
                          const resolvedHref = href?.startsWith('/api')
                            ? href
                            : href;
                          return (
                            <a {...props} href={resolvedHref} target="_blank" rel="noopener noreferrer"
                              style={{ color: 'var(--primary)', textDecoration: 'underline' }}>
                              {children}
                            </a>
                          );
                        },
                        pre({ children, ...props }) {
                          return (
                            <pre style={{ maxWidth: '100%', overflowX: 'auto', margin: 0, padding: 0, background: 'transparent' }} {...props}>
                              {children}
                            </pre>
                          );
                        },
                        code({ inline, className, children, ...props }) {
                          const match = /language-(\w+)/.exec(className || '');
                          const ext = match ? match[1] : 'txt';
                          if (!inline) {
                            return (
                              <div className="code-block">
                                <div className="code-block__header">
                                  <span className="code-block__lang">{ext}</span>
                                  <button
                                    className="code-block__copy"
                                    onClick={() => downloadContent(String(children).replace(/\n$/, ''), `code.${ext}`)}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24"
                                      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                      <polyline points="7 10 12 15 17 10"/>
                                      <line x1="12" y1="15" x2="12" y2="3"/>
                                    </svg>
                                    Download
                                  </button>
                                </div>
                                <pre><code className={className} {...props}>{children}</code></pre>
                              </div>
                            );
                          }
                          return <code className={className} {...props}>{children}</code>;
                        },
                      }}
                    >
                      {preprocessMath(msg.content)}
                    </ReactMarkdown>
                  </div>
                )}
              </div>
            </div>
          ))}

          <div ref={messagesEndRef} />
        </main>

        {/* ── Footer / Input ───────────────────────────────────────── */}
        <footer className="chat-footer">
          <form className="chat-input-wrap" onSubmit={sendMessage}>

            {/* Doc type menu */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <button
                type="button"
                className="doc-trigger"
                onClick={() => setShowDocMenu(v => !v)}
                title="เลือกประเภทเอกสาร"
              >
                +
              </button>
              {showDocMenu && (
                <div className="doc-menu">
                  <div className="doc-menu__header">ประเภทเอกสาร</div>
                  {['บันทึกข้อความ', 'แผนการสอน', 'แบบฝึกหัด', 'ข้อสอบ', 'โครงการ'].map(type => (
                    <div key={type} className="doc-menu__item" onClick={() => handleDocSelect(type)}>
                      📄 {type}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Text input box */}
            <div className="input-box">
              <textarea
                ref={textareaRef}
                className="chat-textarea"
                value={inputMessage}
                onChange={e => setInputMessage(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(e); }
                }}
                placeholder="พิมพ์ข้อความเพื่อเริ่มการสนทนา…"
                disabled={isLoading}
                rows={1}
              />
              {/* Mic */}
              <button
                type="button"
                className={`mic-btn${isListening ? ' mic-btn--active' : ''}`}
                onClick={toggleListen}
                disabled={isLoading}
                title={isListening ? 'หยุดฟัง' : 'พูด'}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" y1="19" x2="12" y2="22"/>
                </svg>
              </button>
            </div>

            {/* Send */}
            <button
              type="submit"
              className="send-btn"
              disabled={isLoading || !inputMessage.trim()}
              title="ส่ง"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
                style={{ width: 18, height: 18, transform: 'translateX(1px)' }}>
                <path d="M3.478 2.404a.75.75 0 0 0-.926.941l2.432 7.905H13.5a.75.75 0 0 1 0 1.5H4.984l-2.432 7.905a.75.75 0 0 0 .926.94 60.519 60.519 0 0 0 18.445-8.986.75.75 0 0 0 0-1.218A60.517 60.517 0 0 0 3.478 2.404Z"/>
              </svg>
            </button>
          </form>

          <p className="chat-disclaimer">
            alexAI อาจให้ข้อมูลที่ผิดพลาดได้ โปรดตรวจสอบความถูกต้องอีกครั้ง
          </p>
        </footer>
      </div>

      {/* Memo Form Modal */}
      {showMemoModal && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div className="modal-content" style={{ background: 'var(--bg-card, #fff)', color: 'var(--text-main, #333)', padding: '20px', borderRadius: '8px', width: '90%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
            <h3 style={{ marginTop: 0, marginBottom: '15px', color: 'var(--text-main)' }}>ฟอร์มสร้างบันทึกข้อความ</h3>
            <form onSubmit={handleMemoSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div><label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9rem' }}>วันที่:</label><input type="text" value={memoFormData.date} onChange={e => setMemoFormData({...memoFormData, date: e.target.value})} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} placeholder="เช่น 21 สิงหาคม 2569" /></div>
              <div><label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9rem' }}>เรื่อง:</label><input type="text" value={memoFormData.subject} onChange={e => setMemoFormData({...memoFormData, subject: e.target.value})} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} /></div>
              <div><label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9rem' }}>เรียน:</label><input type="text" value={memoFormData.to_person} onChange={e => setMemoFormData({...memoFormData, to_person: e.target.value})} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} placeholder="เช่น ผู้อำนวยการโรงเรียน..." /></div>
              <div><label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9rem' }}>เนื้อหา:</label><textarea rows={5} value={memoFormData.content} onChange={e => setMemoFormData({...memoFormData, content: e.target.value})} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', resize: 'vertical' }} /></div>
              <div><label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9rem' }}>ลงชื่อ:</label><input type="text" value={memoFormData.sign_name} onChange={e => setMemoFormData({...memoFormData, sign_name: e.target.value})} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} /></div>
              <div><label style={{ display: 'block', marginBottom: '4px', fontSize: '0.9rem' }}>ตำแหน่ง:</label><input type="text" value={memoFormData.position} onChange={e => setMemoFormData({...memoFormData, position: e.target.value})} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} /></div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                <button type="button" onClick={() => setShowMemoModal(false)} style={{ padding: '8px 16px', background: '#e0e0e0', color: '#333', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>ยกเลิก</button>
                <button type="submit" style={{ padding: '8px 16px', background: 'var(--primary, #007bff)', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>ตกลง</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatUI;
